import React, { useMemo, useState } from 'react';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ExperimentOutlined,
  PlayCircleOutlined,
  TrophyOutlined,
  BulbOutlined,
  ThunderboltOutlined,
  AppstoreOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MainLayout from '../../components/common/MainLayout';
import { canManageUsers } from '../../types/permissions';
import type { User } from '../../types/permissions';
import { useListScenariosQuery, useStartScenarioMutation } from '../../api/savesV2Api';
import type { ScenarioKind, ScenarioTemplate } from '../../types/saves';

const { Title, Paragraph, Text } = Typography;

const KIND_META: Record<ScenarioKind, { label: string; color: string; icon: React.ReactNode; hint: string }> = {
  tutorial: {
    label: 'Обучение',
    color: 'cyan',
    icon: <ReadOutlined />,
    hint: 'Ступень канонической обучающей лестницы',
  },
  mechanical: {
    label: 'Механический',
    color: 'blue',
    icon: <ThunderboltOutlined />,
    hint: 'Сценарий с чёткими целями: пройди или нет',
  },
  explanatory: {
    label: 'Объяснительный',
    color: 'green',
    icon: <BulbOutlined />,
    hint: 'Сравнение веток: «а что если…»',
  },
  competitive: {
    label: 'Соревновательный',
    color: 'gold',
    icon: <TrophyOutlined />,
    hint: 'Лидерборд по результатам',
  },
  campaign: {
    label: 'Кампания',
    color: 'purple',
    icon: <AppstoreOutlined />,
    hint: 'Связанные сценарии в кампании',
  },
};

type KindFilter = 'all' | ScenarioKind;

const ScenarioCard: React.FC<{
  template: ScenarioTemplate;
  onStart: (template: ScenarioTemplate) => void;
  starting: boolean;
}> = ({ template, onStart, starting }) => {
  const meta = KIND_META[template.kind] ?? {
    label: template.kind,
    color: 'default',
    icon: null,
    hint: '',
  };
  const goalsCount = template.goals?.length ?? 0;

  return (
    <Card
      style={{ height: '100%' }}
      title={
        <Space>
          {meta.icon}
          <Text strong>{template.title || template.slug}</Text>
        </Space>
      }
      extra={
        <Tooltip title={meta.hint}>
          <Tag color={meta.color}>{meta.label}</Tag>
        </Tooltip>
      }
      actions={[
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={starting}
          onClick={() => onStart(template)}
        >
          Запустить
        </Button>,
      ]}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {template.narrative && typeof (template.narrative as Record<string, unknown>).intro === 'string' ? (
          <Paragraph ellipsis={{ rows: 3 }} style={{ marginBottom: 0 }}>
            {String((template.narrative as Record<string, unknown>).intro)}
          </Paragraph>
        ) : (
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Описание не задано
          </Paragraph>
        )}

        <div>
          <Text type="secondary">Длительность: </Text>
          <Text strong>{template.durationDays} дн.</Text>
        </div>
        <div>
          <Text type="secondary">Целей: </Text>
          <Text strong>{goalsCount}</Text>
        </div>
        <div>
          <Text type="secondary">Видимость: </Text>
          <Tag>{template.visibility}</Tag>
        </div>
        <div>
          <Text type="secondary">Slug: </Text>
          <Text code>{template.slug}</Text>
        </div>
        <div>
          <Text type="secondary">Версия: </Text>
          <Text>v{template.version}</Text>
        </div>
      </Space>
    </Card>
  );
};

export default function ScenariosGalleryPage() {
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const user = useSelector((state: { auth: { user: User | null } }) => state.auth.user);
  const isAdmin = canManageUsers(user);

  const queryArgs = useMemo(() => {
    return kindFilter === 'all' ? {} : { kind: kindFilter };
  }, [kindFilter]);

  const { data: scenarios, isLoading, isError, refetch } = useListScenariosQuery(queryArgs);
  const [startScenario, { isLoading: starting }] = useStartScenarioMutation();
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);

  const handleStart = (template: ScenarioTemplate) => {
    Modal.confirm({
      title: `Запустить сценарий «${template.title || template.slug}»?`,
      content: (
        <Space direction="vertical">
          <Text>
            Будет создан новый сейв с типом <Tag color="orange">scenario</Tag>.
          </Text>
          <Text type="secondary">
            Длительность: {template.durationDays} дн. · Версия v{template.version}
          </Text>
        </Space>
      ),
      okText: 'Запустить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          setPendingTemplateId(template.scenarioTemplateId);
          const result = await startScenario({
            templateId: template.scenarioTemplateId,
          }).unwrap();
          message.success('Сценарий запущен');
          if (result?.save?.id) {
            navigate(`/saves/${result.save.id}`);
          }
        } catch (err) {
          const e = err as { data?: { message?: string } };
          message.error(e?.data?.message || 'Не удалось запустить сценарий');
        } finally {
          setPendingTemplateId(null);
        }
      },
    });
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">Главная</Link> },
            { title: <Link to="/saves">Сейвы</Link> },
            { title: 'Каталог сценариев' },
          ]}
        />
      }
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <ExperimentOutlined /> Каталог сценариев
        </Title>
        <Space wrap>
          <Button icon={<PlayCircleOutlined />} onClick={() => navigate('/saves')}>
            Игровые сейвы
          </Button>
          <Button icon={<ThunderboltOutlined />} onClick={() => navigate('/saves?type=free')}>
            Свободная игра
          </Button>
          <Button onClick={() => refetch()}>Обновить</Button>
        </Space>
      </Space>

      <Space wrap style={{ marginBottom: 16 }}>
        <Text type="secondary">Тип:</Text>
        <Segmented
          value={kindFilter}
          onChange={(v) => setKindFilter(v as KindFilter)}
          options={[
            { label: 'Все', value: 'all' },
            { label: 'Обучение', value: 'tutorial' },
            { label: 'Механические', value: 'mechanical' },
            { label: 'Объяснительные', value: 'explanatory' },
            { label: 'Соревновательные', value: 'competitive' },
            { label: 'Кампании', value: 'campaign' },
          ]}
        />
      </Space>

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Не удалось загрузить список сценариев"
        />
      )}

      {isAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Здесь показано содержимое таблицы scenario_templates"
          description="Канонические сценарии живут в бэкендных сидах и попадают в эту таблицу только через публикацию. Если сценария нет в списке — он не опубликован, а не скрыт фильтром."
          action={(
            <Button size="small" onClick={() => navigate('/admin/scenarios')}>
              Сверить с сидами
            </Button>
          )}
        />
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : !scenarios || scenarios.length === 0 ? (
        <Empty description="Сценариев не найдено" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Row gutter={[16, 16]}>
          {scenarios.map((tpl) => (
            <Col key={`${tpl.scenarioTemplateId}-${tpl.version}`} xs={24} md={12} xl={8}>
              <ScenarioCard
                template={tpl}
                onStart={handleStart}
                starting={starting && pendingTemplateId === tpl.scenarioTemplateId}
              />
            </Col>
          ))}
        </Row>
      )}
    </MainLayout>
  );
}
